// The Vivarango customer app: routing (History API), the shell for each layout, sign-in state, automatic
// sign-out, offline, theme and language.
import { render, type ComponentChildren, type JSX } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import type { ConsentSource, Dashboard, FlowView, LabView, Me } from '../../../packages/contract/index.ts'
import { STRINGS, fill, type Lang } from './i18n.ts'
import { ApiError, NetworkError, api, fmtTime, listen, mmss, store } from './lib.ts'
import { Activate, ActivateSent, Code, Deleted, LinkPick, Passkey, Problem, Signin, SigninCode, Signup, Welcome } from './auth.tsx'
import { ConsentScreen, Connect, Connected, DexcomGo, LabConfirm, LabReview, LabUpload, ProfileScreen } from './onboarding.tsx'
import { Aside, Claim, Help, Home, PolicyScreen, RewardsScreen, Settings, SummaryScreen, userInitials } from './screens.tsx'
import { Brand, Ctx, ErrorState, Icon, Sheet, Skeleton, Toast, type App, type IconName, type Problem as ProblemState } from './ui.tsx'

type Kind = 'auth' | 'onb' | 'main'
type Route = { readonly kind: Kind; readonly step: number; readonly back?: string; readonly view: () => JSX.Element; readonly needs?: 'me' | 'flow' }

const TABS: readonly { readonly path: string; readonly icon: IconName }[] = [
  { path: '/home', icon: 'home' }, { path: '/summary', icon: 'summary' }, { path: '/rewards', icon: 'rewards' }, { path: '/policy', icon: 'policy' }, { path: '/settings', icon: 'settings' },
]
const SOURCES: readonly ConsentSource[] = ['program', 'dexcom', 'lab']
const PUBLIC = new Set(['/signin', '/signin/code', '/activate', '/activate/sent', '/problem', '/deleted'])

function routeOf(path: string, flow: FlowView | null, me: Me | null): Route | null {
  const consent = /^\/consent\/(program|dexcom|lab)$/.exec(path)?.[1] as ConsentSource | undefined
  if (consent) {
    const dexcomStep = me?.profile?.sensor === 'g6' || me?.profile?.sensor === 'g7'
    const back = consent === 'program' ? '/profile' : consent === 'dexcom' || !dexcomStep ? '/consent/program' : '/consent/dexcom'
    return { kind: 'onb', step: 3, back, view: () => <ConsentScreen source={consent} />, needs: 'me' }
  }
  const codeBack = flow?.purpose === 'signin' ? '/signin/code' : flow?.policyNumber ? '/verify' : '/activate/sent'
  const routes: Record<string, Route> = {
    '/welcome': { kind: 'auth', step: 0, view: () => <Welcome /> },
    '/signup': { kind: 'auth', step: 1, back: '/welcome', view: () => <Signup />, needs: 'flow' },
    '/verify': { kind: 'auth', step: 1, back: '/signup', view: () => <LinkPick />, needs: 'flow' },
    '/code': { kind: 'auth', step: flow?.purpose === 'signin' ? 0 : 1, back: codeBack, view: () => <Code />, needs: 'flow' },
    '/passkey': { kind: 'onb', step: 1, view: () => <Passkey />, needs: 'me' },
    '/profile': { kind: 'onb', step: 2, view: () => <ProfileScreen />, needs: 'me' },
    '/connect': { kind: 'onb', step: 4, back: '/consent/lab', view: () => <Connect />, needs: 'me' },
    '/dexcom': { kind: 'onb', step: 4, back: '/connect', view: () => <DexcomGo />, needs: 'me' },
    '/connected': { kind: 'onb', step: 4, view: () => <Connected />, needs: 'me' },
    '/lab': { kind: 'onb', step: 5, back: '/home', view: () => <LabUpload />, needs: 'me' },
    '/lab/confirm': { kind: 'onb', step: 5, back: '/lab', view: () => <LabConfirm />, needs: 'me' },
    '/lab/review': { kind: 'onb', step: 5, view: () => <LabReview />, needs: 'me' },
    '/home': { kind: 'main', step: 0, view: () => <Home />, needs: 'me' },
    '/summary': { kind: 'main', step: 0, view: () => <SummaryScreen />, needs: 'me' },
    '/rewards': { kind: 'main', step: 0, view: () => <RewardsScreen />, needs: 'me' },
    '/policy': { kind: 'main', step: 0, view: () => <PolicyScreen />, needs: 'me' },
    '/claim': { kind: 'main', step: 0, back: '/policy', view: () => <Claim />, needs: 'me' },
    '/settings': { kind: 'main', step: 0, view: () => <Settings />, needs: 'me' },
    '/help': { kind: 'main', step: 0, back: '/settings', view: () => <Help />, needs: 'me' },
    '/signin': { kind: 'auth', step: 0, view: () => <Signin /> },
    '/signin/code': { kind: 'auth', step: 0, back: '/signin', view: () => <SigninCode /> },
    '/activate': { kind: 'auth', step: 0, back: '/signin', view: () => <Activate /> },
    '/activate/sent': { kind: 'auth', step: 0, back: '/activate', view: () => <ActivateSent /> },
    '/problem': { kind: 'auth', step: 0, view: () => <Problem /> },
    '/deleted': { kind: 'auth', step: 0, view: () => <Deleted /> },
  }
  return routes[path] ?? null
}

const systemDark = (): boolean => { try { return window.matchMedia('(prefers-color-scheme: dark)').matches } catch { return false } }
const SKIP_KEY = (me: Me) => `vr-skip:${me.policyNumber}`
const skipsOf = (me: Me): Set<string> => { try { return new Set(JSON.parse(store.get(SKIP_KEY(me)) ?? '[]') as string[]) } catch { return new Set() } }

function TimeoutSheet({ deadline, onStay, onOut }: { readonly deadline: number; readonly onStay: () => void; readonly onOut: () => void }) {
  const t = STRINGS[(document.documentElement.lang === 'en' ? 'en' : 'es') as Lang]
  const [now, setNow] = useState(Date.now())
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer) }, [])
  const left = Math.max(0, Math.ceil((deadline - now) / 1000))
  return (
    <Sheet label={t.toTitle} onClose={null}>
      <div class="v-stack-l" style={{ textAlign: 'center', alignItems: 'center' }}>
        <span class="v-ico v-ico--brand v-ico--lg"><Icon n="clock" /></span>
        <div class="v-stack-s"><h2 class="v-h1" style={{ fontSize: '26px' }}>{t.toTitle}</h2><p class="v-body v-num" aria-live="polite">{fill(t.toBody, { t: mmss(left) })}</p></div>
        <div class="v-stack-s" style={{ width: '100%' }}>
          <button class="v-btn v-btn--primary" type="button" onClick={onStay}>{t.toStay}</button>
          <button class="v-btn v-btn--quiet" type="button" onClick={onOut}>{t.stSignOut}</button>
        </div>
      </div>
    </Sheet>
  )
}

function Root() {
  const [lang, setLangState] = useState<Lang>(() => (store.get('vr-lang') === 'en' ? 'en' : store.get('vr-lang') === 'es' ? 'es' : 'es'))
  const [themePick, setThemePick] = useState<'light' | 'dark' | null>(() => { const v = store.get('vr-theme'); return v === 'light' || v === 'dark' ? v : null })
  const [sysDark, setSysDark] = useState(systemDark)
  const [path, setPath] = useState(location.pathname)
  const [dir, setDir] = useState<1 | -1>(1)
  const [me, setMeState] = useState<Me | null>(null)
  const [flow, setFlow] = useState<FlowView | null>(null)
  const [dash, setDash] = useState<Dashboard | null>(null)
  const [online, setOnline] = useState(navigator.onLine)
  const [loadedAt, setLoadedAt] = useState<Date | null>(null)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [sheetNode, setSheetNode] = useState<ComponentChildren | null>(null)
  const [problem, setProblem] = useState<ProblemState | null>(null)
  const [boot, setBoot] = useState<'loading' | 'ready' | unknown>('loading')
  const [warnAt, setWarnAt] = useState<number | null>(null)
  const meRef = useRef<Me | null>(null)
  const afterConsent = useRef<string | null>(null)
  const lab = useRef<App['lab']['current']>(null)
  const toastTimer = useRef(0)
  const lastActivity = useRef(Date.now())
  const lastCall = useRef(Date.now())
  const firstPath = useRef(true)
  const t = STRINGS[lang]
  const theme = themePick ?? (sysDark ? 'dark' : 'light')

  const nav = (to: string, options: { replace?: boolean; back?: boolean } = {}) => {
    const idx = Number((history.state as { idx?: number } | null)?.idx ?? 0)
    if (options.replace) history.replaceState({ idx }, '', to)
    else history.pushState({ idx: idx + 1 }, '', to)
    setDir(options.back ? -1 : 1)
    setPath(new URL(to, location.origin).pathname)
  }
  const goBack = (fallback: string) => {
    if (Number((history.state as { idx?: number } | null)?.idx ?? 0) > 0) history.back()
    else nav(fallback, { replace: true, back: true })
  }
  const setMe = (next: Me | null) => {
    meRef.current = next
    setMeState(next)
    if (next) { setLoadedAt(new Date()); if (!store.get('vr-lang')) setLangState(next.language) } else setDash(null)
  }
  const toast = (message: string) => {
    window.clearTimeout(toastTimer.current)
    setToastMsg(message)
    toastTimer.current = window.setTimeout(() => setToastMsg(null), 2800)
  }
  const showProblem = (p: ProblemState) => { setSheetNode(null); setProblem(p); nav('/problem', { replace: true }) }
  const skip = (step: string) => { const m = meRef.current; if (!m) return; const s = skipsOf(m); s.add(step); store.set(SKIP_KEY(m), JSON.stringify([...s])) }
  const unskip = (step: string) => { const m = meRef.current; if (!m) return; const s = skipsOf(m); s.delete(step); store.set(SKIP_KEY(m), JSON.stringify([...s])) }
  const nextStep = async (m: Me): Promise<string> => {
    if (!m.profile) return '/profile'
    const skipped = skipsOf(m)
    const status = (s: ConsentSource) => m.consents.find((c) => c.source === s)?.status ?? 'not_given'
    const dexcomSensor = m.profile.sensor === 'g6' || m.profile.sensor === 'g7'
    if (status('program') !== 'given') return '/consent/program'
    if (dexcomSensor && status('dexcom') === 'not_given' && !skipped.has('consent:dexcom')) return '/consent/dexcom'
    if (status('lab') === 'not_given' && !skipped.has('consent:lab')) return '/consent/lab'
    if (m.profile.sensor !== 'none' && m.dexcom.status !== 'connected' && status('dexcom') !== 'withdrawn' && !skipped.has('connect')) return '/connect'
    if (status('lab') === 'given' && !skipped.has('lab')) {
      const labs = await api<readonly LabView[]>('/api/labs').catch(() => [])
      if (labs.length === 0) return '/lab'
    }
    return '/home'
  }
  const signOut = async (why: 'manual' | 'idle' | 'all') => {
    setWarnAt(null)
    setSheetNode(null)
    await api<null>(why === 'all' ? '/api/signout/everywhere' : '/api/signout', { method: 'POST', json: {}, quiet401: true }).catch(() => {})
    setMe(null)
    setFlow(null)
    if (why === 'manual') { nav('/signin', { replace: true }); toast(t.signedOutToast) } else showProblem({ key: why === 'all' ? 'signedOutAll' : 'signedOut' })
  }

  // Server answers: a 401 signedOut while signed in ends the session; network failures show offline.
  listen({
    onSignedOut: () => { if (meRef.current) { setMe(null); showProblem({ key: 'signedOut' }) } },
    onNetwork: (ok) => setOnline(ok && navigator.onLine),
    onCall: () => { lastCall.current = Date.now() },
  })

  const start = async () => {
    setBoot('loading')
    const at = location.pathname
    const token = /^\/activate\/(.+)$/.exec(at)?.[1]
    try {
      if (token && token !== 'sent') {
        history.replaceState({ idx: 0 }, '', '/welcome')
        try {
          setFlow(await api<FlowView>('/api/activation/open', { method: 'POST', json: { token: decodeURIComponent(token) }, quiet401: true }))
          setPath('/welcome')
        } catch (e) {
          if (e instanceof ApiError && ['linkExpired', 'alreadyActive', 'notActive', 'cancelled'].includes(e.code)) { setProblem({ key: e.code as ProblemState['key'] }); history.replaceState({ idx: 0 }, '', '/problem'); setPath('/problem') } else throw e
        }
        setBoot('ready')
        return
      }
      try {
        const m = await api<Me>('/api/me', { quiet401: true })
        setMe(m)
        const r = routeOf(at, null, m)
        const required = await nextStep(m)
        const mustOnboard = required === '/profile' || required === '/consent/program'
        if (!r || r.kind === 'auth' || r.needs === 'flow' || (r.kind === 'main' && mustOnboard)) { history.replaceState({ idx: 0 }, '', required); setPath(required) }
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 401)) throw e
        try {
          const f = await api<FlowView>('/api/flow', { quiet401: true })
          setFlow(f)
          const r = routeOf(at, f, null)
          if (!r || r.needs === 'me' || (r.needs !== 'flow' && at !== '/welcome' && !PUBLIC.has(at))) {
            const to = f.codeSentAt ? '/code' : f.purpose === 'activate' ? '/welcome' : '/signin'
            history.replaceState({ idx: 0 }, '', to)
            setPath(to)
          }
        } catch (e2) {
          if (!(e2 instanceof ApiError && e2.status === 401)) throw e2
          if (!PUBLIC.has(at) || at === '/problem') { history.replaceState({ idx: 0 }, '', '/signin'); setPath('/signin') }
        }
      }
      setBoot('ready')
    } catch (error) { setBoot(error) }
  }

  useEffect(() => {
    void start()
    const onPop = () => { setDir(-1); setPath(location.pathname); setSheetNode(null) }
    const onOnline = () => setOnline(true)
    const onOffline = () => setOnline(false)
    window.addEventListener('popstate', onPop)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    let mq: MediaQueryList | null = null
    const onScheme = (e: MediaQueryListEvent) => setSysDark(e.matches)
    try { mq = window.matchMedia('(prefers-color-scheme: dark)'); mq.addEventListener('change', onScheme) } catch { /* no media queries */ }
    return () => { window.removeEventListener('popstate', onPop); window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); mq?.removeEventListener('change', onScheme) }
  }, [])

  // Theme and language on the document.
  useEffect(() => {
    document.documentElement.dataset['theme'] = theme
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#07141B' : '#F6F8F7')
  }, [theme])
  useEffect(() => { document.documentElement.lang = lang }, [lang])

  // A route that needs a session or a flow it does not have goes to sign in.
  const route = routeOf(path, flow, me)
  useEffect(() => {
    if (boot !== 'ready') return
    if (!route || (route.needs === 'me' && !me) || (route.needs === 'flow' && !flow)) nav(me ? '/home' : '/signin', { replace: true })
  }, [boot, path, me, flow])

  // The desktop right column needs the dashboard on every main screen.
  useEffect(() => {
    if (route?.kind === 'main' && me && !dash && path !== '/home' && path !== '/rewards') void api<Dashboard>('/api/dashboard').then(setDash, () => {})
  }, [path, me])

  // Each new screen: scroll to the top, move focus to its title and name the tab after it.
  useEffect(() => {
    if (boot !== 'ready') return
    const frame = requestAnimationFrame(() => {
      const h1 = document.querySelector<HTMLElement>('main h1')
      if (h1) document.title = `${h1.textContent ?? ''} · Vivarango`
      if (firstPath.current) { firstPath.current = false; return }
      window.scrollTo(0, 0)
      if (h1) { h1.setAttribute('tabindex', '-1'); h1.focus({ preventScroll: true }) }
    })
    return () => cancelAnimationFrame(frame)
  }, [path, boot, lang])

  // Automatic sign-out after me.idleSeconds without taps, keys or scrolls; a notice 60 seconds before.
  useEffect(() => {
    if (!me) { setWarnAt(null); return }
    lastActivity.current = Date.now()
    const onActivity = () => {
      if (warnRef.current !== null) return
      lastActivity.current = Date.now()
      if (Date.now() - lastCall.current > 120_000) { lastCall.current = Date.now(); void api<null>('/api/session/keepalive', { method: 'POST', json: {} }).catch(() => {}) }
    }
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true, capture: true }))
    const timer = window.setInterval(() => {
      const idle = (Date.now() - lastActivity.current) / 1000
      if (idle >= me.idleSeconds) void signOut('idle')
      else if (idle >= me.idleSeconds - 60 && warnRef.current === null) setWarnAt(lastActivity.current + me.idleSeconds * 1000)
    }, 1000)
    return () => { events.forEach((e) => window.removeEventListener(e, onActivity, { capture: true })); window.clearInterval(timer) }
  }, [me?.policyNumber, me?.idleSeconds])
  const warnRef = useRef<number | null>(null)
  warnRef.current = warnAt
  const stay = () => {
    lastActivity.current = Date.now()
    setWarnAt(null)
    void api<null>('/api/session/keepalive', { method: 'POST', json: {} }).catch(() => {})
  }

  const app: App = useMemo(() => ({
    t, lang, theme, path, dir, me, flow, dash, online, problem, afterConsent, lab,
    setLang: (l: Lang) => { store.set('vr-lang', l); setLangState(l) },
    setTheme: (v: 'light' | 'dark') => { store.set('vr-theme', v); setThemePick(v) },
    nav, goBack, setMe, setFlow, setDash, toast, sheet: setSheetNode, showProblem, nextStep, skip, unskip, signOut,
  }), [t, lang, theme, path, dir, me, flow, dash, online, problem])

  const kind: Kind = route?.kind ?? 'auth'
  const main = kind === 'main'
  const step = route?.step ?? 0
  const showLogo = !route?.back && !step && path !== '/welcome' && path !== '/signin'
  const overlay = warnAt !== null || sheetNode !== null

  let content: ComponentChildren
  if (boot === 'loading') content = <Skeleton />
  else if (boot !== 'ready') content = <ErrorState error={boot instanceof NetworkError ? boot : new Error('boot')} onRetry={() => void start()} />
  else if (route && !(route.needs === 'me' && !me) && !(route.needs === 'flow' && !flow)) content = <section key={path} class={`v-screen ${dir < 0 ? 'is-back' : ''} ${main ? 'v-stack' : 'v-stack-l'}`}>{route.view()}</section>
  else content = <Skeleton />

  return (
    <Ctx.Provider value={app}>
      <div class="v-app" data-theme={theme} lang={lang}>
        <div class={`v-shell ${main ? 'is-main' : 'is-onb'}`} inert={overlay}>
          {main && me && (
            <nav class="v-rail" aria-label={t.navLabel}>
              <span style={{ padding: '4px 8px 0' }}><Brand variant="stack" /></span>
              <div class="v-stack-s" style={{ gap: '4px' }}>
                {TABS.map((tab, i) => <button key={tab.path} class="v-nav-i" type="button" aria-current={path === tab.path || (tab.path === '/policy' && path === '/claim') || (tab.path === '/settings' && path === '/help') ? 'page' : undefined} onClick={() => nav(tab.path)}><Icon n={tab.icon} /><span>{t.tabs[i]}</span></button>)}
              </div>
              <div class="v-stack-s" style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px' }}>
                  <span class="v-avatar" aria-hidden="true">{userInitials(me)}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}><span class="v-strong" style={{ fontSize: '14px' }}>{me.firstName} {me.lastName}</span><span class="v-xs v-num">{me.policyNumber}</span></span>
                </div>
                <span class="v-powered" style={{ padding: '0 8px' }}>Powered by <b>Sherpa+</b></span>
              </div>
            </nav>
          )}
          {!main && (
            <aside class="v-onb-side" aria-label="Vivarango">
              <div class="v-stack-l">
                <Brand variant="lg" />
                <div class="v-stack-s" style={{ marginTop: '24px' }}><h2 class="v-h1" style={{ fontSize: '30px' }}>{t.sideTitle}</h2><p class="v-body">{t.sideBody}</p></div>
                {step > 0 ? (
                  <ol class="v-onb-steps" aria-label={fill(t.stepOf, { n: step })}>
                    {t.onbSteps.map((label, i) => <li key={label} class={`v-onb-step ${i + 1 < step ? 'is-done' : i + 1 === step ? 'is-now' : ''}`} aria-current={i + 1 === step ? 'step' : undefined}><b>{i + 1 < step ? <Icon n="check" s={16} /> : i + 1}</b><span>{label}</span></li>)}
                  </ol>
                ) : (
                  <ul class="v-stack">
                    {t.trust.map((tr) => <li key={tr} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', fontSize: '15px' }}><span class="v-ico v-ico--brand" style={{ width: '32px', height: '32px', borderRadius: '10px' }}><Icon n="check" s={16} /></span><span style={{ paddingTop: '4px' }}>{tr}</span></li>)}
                  </ul>
                )}
              </div>
              <div class="v-stack">
                <svg class="v-hero-art" viewBox="0 0 360 150" aria-hidden="true" style={{ maxWidth: '360px' }}>
                  <rect x="0" y="0" width="360" height="150" rx="22" style={{ fill: 'var(--surface)' }} />
                  <rect x="28" y="86" width="18" height="38" rx="9" style={{ fill: 'var(--prog-track)' }} /><rect x="58" y="70" width="18" height="54" rx="9" style={{ fill: 'var(--prog-track)' }} />
                  <rect x="88" y="78" width="18" height="46" rx="9" style={{ fill: 'var(--q2)' }} /><rect x="118" y="58" width="18" height="66" rx="9" style={{ fill: 'var(--q2)' }} />
                  <rect x="148" y="64" width="18" height="60" rx="9" style={{ fill: 'var(--prog)' }} /><rect x="178" y="44" width="18" height="80" rx="9" style={{ fill: 'var(--prog)' }} /><rect x="208" y="50" width="18" height="74" rx="9" style={{ fill: 'var(--prog)' }} />
                  <circle cx="292" cy="72" r="34" style={{ fill: 'none', stroke: 'var(--prog-track)', strokeWidth: 14 }} />
                  <path d="M292 38 A34 34 0 1 1 260.1 83.6" style={{ fill: 'none', stroke: 'var(--prog)', strokeWidth: 14, strokeLinecap: 'round' }} />
                </svg>
                <div class="v-msg v-msg--brand"><Icon n="eyeOff" /><span>{t.wPrivacy}</span></div>
              </div>
            </aside>
          )}
          <div class="v-col">
            <header class="v-top">
              {route?.back && <button class="v-icon-btn v-back" type="button" aria-label={t.back} onClick={() => goBack(route.back ?? '/signin')}><Icon n="back" s={24} /></button>}
              {showLogo && <Brand />}
              {step > 0 && <div class="v-steps" role="img" aria-label={fill(t.stepOf, { n: step })}>{[1, 2, 3, 4, 5].map((n) => <i key={n} class={n <= step ? 'on' : ''} />)}</div>}
              <div class="v-top-end">
                <div class="v-seg" role="group" aria-label={t.langLabel}>
                  <button type="button" aria-pressed={lang === 'es'} onClick={() => app.setLang('es')} lang="es">ES</button>
                  <button type="button" aria-pressed={lang === 'en'} onClick={() => app.setLang('en')} lang="en">EN</button>
                </div>
                <button class="v-icon-btn" type="button" aria-label={theme === 'dark' ? t.toLight : t.toDark} onClick={() => app.setTheme(theme === 'dark' ? 'light' : 'dark')}><Icon n={theme === 'dark' ? 'sun' : 'moon'} /></button>
              </div>
            </header>
            {!online && <div class="v-banner" role="status"><Icon n="offline" s={16} /><span>{me && loadedAt ? fill(t.offline, { t: fmtTime(loadedAt, lang) }) : t.offlineNoData}</span></div>}
            <main class="v-main">{content}</main>
            <footer class="v-foot">
              <p class="v-xs">{t.disclaimer}</p>
              <span class="v-powered">Powered by <b>Sherpa+</b></span>
            </footer>
            {main && me && (
              <nav class="v-tabbar" aria-label={t.navLabel}>
                {TABS.map((tab, i) => <button key={tab.path} class="v-tab" type="button" aria-current={path === tab.path || (tab.path === '/policy' && path === '/claim') || (tab.path === '/settings' && path === '/help') ? 'page' : undefined} onClick={() => nav(tab.path)}><Icon n={tab.icon} s={24} /><span>{t.tabs[i]}</span></button>)}
              </nav>
            )}
          </div>
          {main && me && <Aside dash={dash} />}
        </div>
        {sheetNode}
        {warnAt !== null && <TimeoutSheet deadline={warnAt} onStay={stay} onOut={() => void signOut('manual')} />}
        {toastMsg && <Toast message={toastMsg} />}
      </div>
    </Ctx.Provider>
  )
}

render(<Root />, document.getElementById('app') ?? document.body)

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => { void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {}) })
}

export { SOURCES }
